import { useRef } from "react";
import useFocoModal from "../../hooks/useFocoModal";
import { pct } from "../../utils";

export default function PdfPreviewOverlay({project, tasks, agenda, onClose}) {
  const cajaRef = useRef(null);
  useFocoModal(cajaRef, true, onClose);
  const projAgenda = agenda.filter(a=>String(a.project_id)===String(project?.id));
  const totalH     = projAgenda.reduce((s,a)=>s+a.hours,0);
  const completed  = tasks.filter(t=>t.status==="Completada").length;
  const blocked    = tasks.filter(t=>t.status==="Bloqueada").length;
  const progress   = pct(tasks);
  const ST_C = {"Completada":"#22c55e","En progreso":"#3b82f6","Bloqueada":"#ef4444","Pendiente":"#94a3b8","En Espera":"#f59e0b"};
  const PR_C = {"Alta":"#ef4444","Media":"#f59e0b","Baja":"#22c55e"};
  const byType = {};
  projAgenda.forEach(a=>{ const tp=a.type||"Otros"; if(!byType[tp]) byType[tp]=[]; byType[tp].push(a); });
  const fmtD = d => d ? new Date(d).toLocaleDateString("es-ES",{day:"2-digit",month:"short"}) : "—";
  const today = new Date().toLocaleDateString("es-ES",{day:"2-digit",month:"long",year:"numeric"});

  return (
    <div ref={cajaRef} role="dialog" aria-modal="true" aria-label="Vista previa del informe del proyecto" style={{position:"fixed",inset:0,background:"var(--surface2)",zIndex:9999,display:"flex",flexDirection:"column",fontFamily:"Arial,Helvetica,sans-serif",fontSize:12,color:"#1e293b"}}>
      <style>{`@media print{.pdf-no-print{display:none!important}@page{margin:14mm 12mm}body{background:var(--surface)}}`}</style>

      {/* Barra superior — oculta al imprimir */}
      <div className="pdf-no-print" style={{background:"var(--surface)",borderBottom:"1px solid #e2e8f0",padding:"10px 20px",display:"flex",alignItems:"center",gap:12,flexShrink:0,boxShadow:"0 1px 4px rgba(0,0,0,.06)"}}>
        <button onClick={onClose}
          style={{display:"flex",alignItems:"center",gap:6,padding:"7px 14px",borderRadius:8,border:"1px solid #e2e8f0",cursor:"pointer",fontSize:12,background:"var(--surface2)",color:"#475569",fontFamily:"inherit",fontWeight:600}}>
          ← Volver
        </button>
        <span style={{flex:1,fontWeight:700,fontSize:14,color:"#1e293b"}}>
          Vista previa del informe
          <span style={{fontWeight:400,color:"#64748b",marginLeft:8,fontSize:12}}>· {project?.name}</span>
        </span>
        <button onClick={()=>window.print()}
          style={{display:"flex",alignItems:"center",gap:7,padding:"8px 20px",borderRadius:8,border:"none",cursor:"pointer",fontSize:13,fontWeight:700,background:"linear-gradient(135deg,#086C76,#087DAB)",color:"#fff",fontFamily:"inherit",boxShadow:"0 2px 8px #086C7640"}}>
          🖨️ Imprimir / Guardar PDF
        </button>
      </div>

      {/* Contenido del informe con scroll */}
      <div style={{flex:1,overflowY:"auto",padding:"28px 40px",background:"var(--surface2)"}}>
        <div style={{maxWidth:900,margin:"0 auto",background:"var(--surface)",borderRadius:12,boxShadow:"0 4px 24px rgba(0,0,0,.08)",overflow:"hidden"}}>

          {/* Cabecera del informe */}
          <div style={{background:"linear-gradient(135deg,#0f172a 60%,#1e3a5f)",color:"#fff",padding:"28px 32px",position:"relative",overflow:"hidden"}}>
            <div style={{position:"absolute",right:-20,top:-20,width:160,height:160,borderRadius:"50%",background:"rgba(0,180,216,.12)"}}/>
            <div style={{position:"relative"}}>
              <div style={{fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:".12em",color:"#7dd3fc",marginBottom:6}}>Informe de Proyecto</div>
              <div style={{fontSize:24,fontWeight:800,marginBottom:4}}>{project?.name}</div>
              {project?.desc&&<div style={{fontSize:12,color:"#94a3b8",marginBottom:4}}>{project?.desc}</div>}
              <div style={{fontSize:11,color:"#64748b"}}>Generado el {today}</div>
            </div>
          </div>

          <div style={{padding:"28px 32px"}}>
            {/* KPIs */}
            <div style={{display:"grid",gridTemplateColumns:"repeat(6,1fr)",gap:10,marginBottom:28}}>
              {[
                ["Total tareas",tasks.length,"#1e293b"],
                ["Completadas",completed,"#22c55e"],
                ["Bloqueadas",blocked,"#ef4444"],
                ["Progreso",progress+"%","#3b82f6"],
                ["Horas Cuaderno",totalH.toFixed(1)+"h","#8b5cf6"],
                ["Miembros",project?.members?.length||0,"#f59e0b"],
              ].map(([l,v,c])=>(
                <div key={l} style={{border:"1px solid #e2e8f0",borderRadius:10,padding:"14px 8px",textAlign:"center",background:"var(--surface2)"}}>
                  <div style={{fontSize:22,fontWeight:800,color:c,lineHeight:1}}>{v}</div>
                  <div style={{fontSize:10,color:"#64748b",textTransform:"uppercase",letterSpacing:".05em",marginTop:5}}>{l}</div>
                </div>
              ))}
            </div>

            {/* Tabla tareas */}
            <div style={{fontWeight:700,fontSize:11,textTransform:"uppercase",letterSpacing:".07em",color:"#475569",borderBottom:"2px solid #e2e8f0",paddingBottom:6,marginBottom:12}}>
              Tareas del proyecto
            </div>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:12,marginBottom:28}}>
              <thead><tr style={{background:"var(--surface2)"}}>
                {["Tarea","Estado","Prioridad","Encargado","Inicio","Vence"].map(h=>(
                  <th key={h} style={{padding:"8px 11px",textAlign:"left",fontWeight:700,fontSize:10,color:"#64748b",borderBottom:"2px solid #e2e8f0",textTransform:"uppercase",letterSpacing:".04em"}}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {tasks.map((t,i)=>(
                  <tr key={t.id} style={{background:i%2?"#fafafa":"#fff",borderBottom:"1px solid #f1f5f9"}}>
                    <td style={{padding:"8px 11px",fontWeight:600}}>{t.title}</td>
                    <td style={{padding:"8px 11px"}}><span style={{padding:"3px 8px",borderRadius:12,fontSize:10,fontWeight:700,background:(ST_C[t.status]||"#94a3b8")+"20",color:ST_C[t.status]||"#94a3b8"}}>{t.status}</span></td>
                    <td style={{padding:"8px 11px"}}><span style={{padding:"3px 8px",borderRadius:12,fontSize:10,fontWeight:700,background:(PR_C[t.priority]||"#94a3b8")+"20",color:PR_C[t.priority]||"#94a3b8"}}>{t.priority||"—"}</span></td>
                    <td style={{padding:"8px 11px",color:"#475569"}}>{t.assignee||"Sin asignar"}</td>
                    <td style={{padding:"8px 11px",color:"#64748b"}}>{fmtD(t.start)}</td>
                    <td style={{padding:"8px 11px",color:"#64748b"}}>{fmtD(t.due)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Cuaderno */}
            {projAgenda.length>0&&(
              <div>
                <div style={{fontWeight:700,fontSize:11,textTransform:"uppercase",letterSpacing:".07em",color:"#475569",borderBottom:"2px solid #e2e8f0",paddingBottom:6,marginBottom:12}}>
                  Cuaderno — {totalH.toFixed(1)}h
                </div>
                {Object.entries(byType).map(([tp,es])=>(
                  <div key={tp} style={{marginBottom:16}}>
                    <div style={{background:"var(--surface2)",padding:"6px 11px",borderRadius:"7px 7px 0 0",fontWeight:700,fontSize:12,display:"flex",justifyContent:"space-between",color:"#475569"}}>
                      <span>{tp}</span>
                      <span style={{color:"#3b82f6",fontWeight:800}}>{es.reduce((s,a)=>s+a.hours,0).toFixed(1)}h</span>
                    </div>
                    <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
                      <thead><tr style={{background:"var(--surface2)"}}>
                        {["Fecha","Descripción","Departamento","Horas"].map(h=>(
                          <th key={h} style={{padding:"6px 11px",textAlign:"left",fontWeight:700,fontSize:10,color:"#64748b",borderBottom:"1px solid #e2e8f0",textTransform:"uppercase",letterSpacing:".04em"}}>{h}</th>
                        ))}
                      </tr></thead>
                      <tbody>
                        {es.map((a,i)=>(
                          <tr key={a.id} style={{background:i%2?"#fafafa":"#fff",borderBottom:"1px solid #f1f5f9"}}>
                            <td style={{padding:"6px 11px",whiteSpace:"nowrap",color:"#64748b"}}>{fmtD(a.entry_date)}</td>
                            <td style={{padding:"6px 11px"}}>{a.description}</td>
                            <td style={{padding:"6px 11px",color:"#64748b"}}>{a.department}</td>
                            <td style={{padding:"6px 11px",fontWeight:700,color:"#3b82f6"}}>{a.hours}h</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            )}

            <div style={{textAlign:"center",fontSize:10,color:"#94a3b8",marginTop:16,paddingTop:12,borderTop:"1px solid #e2e8f0"}}>
              ALM Manager · {project?.name} · {today}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
